import { call, put, takeEvery, takeLatest, all, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { 
  accountApi, 
  ChangeEmailRequest, 
  ChangePasswordRequest, 
  UpdatePaymentMethodRequest, 
  User 
} from '../../services/accountApi';
import {
  setLoading,
  setError,
  setAccountInfo,
  setUser,
  setSubscription,
  setPaymentMethods,
  setDefaultPaymentMethod,
  setBillingHistory,
  setChangeEmailDialogOpen,
  setChangePasswordDialogOpen,
  setUpdatePaymentMethodDialogOpen,
  setCancelSubscriptionDialogOpen,
  setChangeEmailValidationErrors,
  setChangePasswordValidationErrors,
  setUpdatePaymentMethodValidationErrors,
  setSuccessMessage,
  setBillingPagination,
  setOperationLoading,
  FETCH_ACCOUNT_INFO,
  FETCH_USER_PROFILE,
  UPDATE_USER_PROFILE,
  CHANGE_EMAIL,
  CHANGE_PASSWORD,
  FETCH_SUBSCRIPTION,
  CANCEL_SUBSCRIPTION,
  REACTIVATE_SUBSCRIPTION,
  FETCH_PAYMENT_METHODS,
  UPDATE_PAYMENT_METHOD,
  ADD_PAYMENT_METHOD,
  DELETE_PAYMENT_METHOD,
  SET_DEFAULT_PAYMENT_METHOD,
  FETCH_BILLING_HISTORY,
  DOWNLOAD_INVOICE,
  CLOSE_ACCOUNT,
} from '../reducers/accountSlice';
import { RootState } from '../index';

// Worker Sagas
function* fetchAccountInfoSaga(): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const accountInfo = yield call(accountApi.getAccountInfo);
    yield put(setAccountInfo(accountInfo));
  } catch (error: any) {
    console.error('Error fetching account info:', error);
    yield put(setError(error.message || 'Failed to fetch account information'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchUserProfileSaga(): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const user = yield call(accountApi.getUserProfile);
    yield put(setUser(user));
  } catch (error: any) {
    console.error('Error fetching user profile:', error);
    yield put(setError(error.message || 'Failed to fetch user profile'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateUserProfileSaga(action: PayloadAction<Partial<User>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const updatedUser = yield call(accountApi.updateUserProfile, action.payload);
    yield put(setUser(updatedUser));
    yield put(setSuccessMessage({ type: 'emailChanged', message: 'Profile updated successfully' }));
  } catch (error: any) {
    console.error('Error updating user profile:', error);
    yield put(setError(error.message || 'Failed to update profile'));
  } finally {
    yield put(setLoading(false));
  }
}

function* changeEmailSaga(action: PayloadAction<ChangeEmailRequest>): Generator<any, void, any> {
  try {
    yield put(setOperationLoading({ operation: 'changingEmail', loading: true }));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(accountApi.validation.validateChangeEmail, action.payload);
    if (validationErrors.length > 0) {
      yield put(setChangeEmailValidationErrors(validationErrors));
      return;
    }
    
    const response = yield call(accountApi.changeEmail, action.payload);
    if (response.success) {
      yield put(setChangeEmailDialogOpen(false));
      yield put(setSuccessMessage({ type: 'emailChanged', message: response.message }));
      // Refresh user profile to get updated email
      yield call(fetchUserProfileSaga);
    } else {
      yield put(setError(response.message || 'Failed to change email'));
    }
  } catch (error: any) {
    console.error('Error changing email:', error);
    yield put(setError(error.message || 'Failed to change email'));
  } finally {
    yield put(setOperationLoading({ operation: 'changingEmail', loading: false }));
  }
}

function* changePasswordSaga(action: PayloadAction<ChangePasswordRequest>): Generator<any, void, any> {
  try {
    yield put(setOperationLoading({ operation: 'changingPassword', loading: true }));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(accountApi.validation.validateChangePassword, action.payload);
    if (validationErrors.length > 0) {
      yield put(setChangePasswordValidationErrors(validationErrors));
      return;
    }
    
    const response = yield call(accountApi.changePassword, action.payload);
    if (response.success) {
      yield put(setChangePasswordDialogOpen(false));
      yield put(setSuccessMessage({ type: 'passwordChanged', message: response.message }));
    } else {
      yield put(setError(response.message || 'Failed to change password'));
    }
  } catch (error: any) {
    console.error('Error changing password:', error);
    yield put(setError(error.message || 'Failed to change password'));
  } finally {
    yield put(setOperationLoading({ operation: 'changingPassword', loading: false }));
  }
}

function* fetchSubscriptionSaga(): Generator<any, void, any> {
  try {
    const subscription = yield call(accountApi.getSubscription);
    yield put(setSubscription(subscription));
  } catch (error: any) {
    console.error('Error fetching subscription:', error);
    yield put(setError(error.message || 'Failed to fetch subscription'));
  }
}

function* cancelSubscriptionSaga(): Generator<any, void, any> {
  try {
    yield put(setOperationLoading({ operation: 'cancellingSubscription', loading: true }));
    yield put(setError(null));
    
    const response = yield call(accountApi.cancelSubscription);
    if (response.success) {
      yield put(setCancelSubscriptionDialogOpen(false));
      yield put(setSuccessMessage({ type: 'subscriptionCancelled', message: response.message }));
      // Refresh subscription to get updated status
      yield call(fetchSubscriptionSaga);
    } else {
      yield put(setError(response.message || 'Failed to cancel subscription'));
    }
  } catch (error: any) {
    console.error('Error cancelling subscription:', error);
    yield put(setError(error.message || 'Failed to cancel subscription'));
  } finally {
    yield put(setOperationLoading({ operation: 'cancellingSubscription', loading: false }));
  }
}

function* reactivateSubscriptionSaga(): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(accountApi.reactivateSubscription);
    if (response.success) {
      yield put(setSuccessMessage({ type: 'subscriptionCancelled', message: response.message }));
      // Refresh subscription to get updated status
      yield call(fetchSubscriptionSaga);
    } else {
      yield put(setError(response.message || 'Failed to reactivate subscription'));
    }
  } catch (error: any) {
    console.error('Error reactivating subscription:', error);
    yield put(setError(error.message || 'Failed to reactivate subscription'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchPaymentMethodsSaga(): Generator<any, void, any> {
  try {
    const [paymentMethods, defaultPaymentMethod] = yield all([
      call(accountApi.getPaymentMethods),
      call(accountApi.getDefaultPaymentMethod)
    ]);
    
    yield put(setPaymentMethods(paymentMethods));
    yield put(setDefaultPaymentMethod(defaultPaymentMethod));
  } catch (error: any) {
    console.error('Error fetching payment methods:', error);
    yield put(setError(error.message || 'Failed to fetch payment methods'));
  }
}

function* updatePaymentMethodSaga(action: PayloadAction<UpdatePaymentMethodRequest>): Generator<any, void, any> {
  try {
    yield put(setOperationLoading({ operation: 'updatingPaymentMethod', loading: true }));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(accountApi.validation.validatePaymentMethod, action.payload);
    if (validationErrors.length > 0) {
      yield put(setUpdatePaymentMethodValidationErrors(validationErrors));
      return;
    }
    
    const response = yield call(accountApi.updatePaymentMethod, action.payload);
    if (response.success) {
      yield put(setUpdatePaymentMethodDialogOpen(false));
      yield put(setSuccessMessage({ type: 'paymentMethodUpdated', message: response.message }));
      // Refresh payment methods to get updated information
      yield call(fetchPaymentMethodsSaga);
    } else {
      yield put(setError(response.message || 'Failed to update payment method'));
    }
  } catch (error: any) {
    console.error('Error updating payment method:', error);
    yield put(setError(error.message || 'Failed to update payment method'));
  } finally {
    yield put(setOperationLoading({ operation: 'updatingPaymentMethod', loading: false }));
  }
}

function* addPaymentMethodSaga(action: PayloadAction<UpdatePaymentMethodRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(accountApi.validation.validatePaymentMethod, action.payload);
    if (validationErrors.length > 0) {
      yield put(setUpdatePaymentMethodValidationErrors(validationErrors));
      return;
    }
    
    const newPaymentMethod = yield call(accountApi.addPaymentMethod, action.payload);
    yield put(setSuccessMessage({ type: 'paymentMethodUpdated', message: 'Payment method added successfully' }));
    // Refresh payment methods to include the new one
    yield call(fetchPaymentMethodsSaga);
  } catch (error: any) {
    console.error('Error adding payment method:', error);
    yield put(setError(error.message || 'Failed to add payment method'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deletePaymentMethodSaga(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(accountApi.deletePaymentMethod, action.payload);
    if (response.success) {
      yield put(setSuccessMessage({ type: 'paymentMethodUpdated', message: response.message }));
      // Refresh payment methods to remove the deleted one
      yield call(fetchPaymentMethodsSaga);
    } else {
      yield put(setError(response.message || 'Failed to delete payment method'));
    }
  } catch (error: any) {
    console.error('Error deleting payment method:', error);
    yield put(setError(error.message || 'Failed to delete payment method'));
  } finally {
    yield put(setLoading(false));
  }
}

function* setDefaultPaymentMethodSaga(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(accountApi.setDefaultPaymentMethod, action.payload);
    if (response.success) {
      yield put(setSuccessMessage({ type: 'paymentMethodUpdated', message: response.message }));
      // Refresh payment methods to update default status
      yield call(fetchPaymentMethodsSaga);
    } else {
      yield put(setError(response.message || 'Failed to set default payment method'));
    }
  } catch (error: any) {
    console.error('Error setting default payment method:', error);
    yield put(setError(error.message || 'Failed to set default payment method'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchBillingHistorySaga(action: PayloadAction<{ page?: number; limit?: number } | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const billingHistory = yield call(accountApi.getBillingHistory, params);
    
    yield put(setBillingHistory(billingHistory.invoices));
    yield put(setBillingPagination({
      total: billingHistory.total,
      page: params.page || 1,
      pageSize: params.limit || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching billing history:', error);
    yield put(setError(error.message || 'Failed to fetch billing history'));
  } finally {
    yield put(setLoading(false));
  }
}

function* downloadInvoiceSaga(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const blob = yield call(accountApi.downloadInvoice, action.payload);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoice-${action.payload}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error('Error downloading invoice:', error);
    yield put(setError(error.message || 'Failed to download invoice'));
  } finally {
    yield put(setLoading(false));
  }
}

function* closeAccountSaga(action: PayloadAction<string | undefined>): Generator<any, void, any> {
  try {
    yield put(setOperationLoading({ operation: 'closingAccount', loading: true }));
    yield put(setError(null));
    
    const response = yield call(accountApi.closeAccount, action.payload);
    if (response.success) {
      yield put(setSuccessMessage({ type: 'emailChanged', message: response.message }));
      // Redirect to login or home page might be handled at component level
    } else {
      yield put(setError(response.message || 'Failed to close account'));
    }
  } catch (error: any) {
    console.error('Error closing account:', error);
    yield put(setError(error.message || 'Failed to close account'));
  } finally {
    yield put(setOperationLoading({ operation: 'closingAccount', loading: false }));
  }
}

// Helper saga to auto-fetch related data
function* autoFetchAccountData(): Generator<any, void, any> {
  try {
    yield all([
      call(fetchAccountInfoSaga),
      call(fetchBillingHistorySaga, { type: FETCH_BILLING_HISTORY, payload: { page: 1, limit: 10 } }),
    ]);
  } catch (error) {
    // Ignore errors in auto-fetch
  }
}

// Watcher Sagas
function* watchFetchAccountInfo(): Generator<any, void, any> {
  yield takeLatest(FETCH_ACCOUNT_INFO, fetchAccountInfoSaga);
}

function* watchFetchUserProfile(): Generator<any, void, any> {
  yield takeLatest(FETCH_USER_PROFILE, fetchUserProfileSaga);
}

function* watchUpdateUserProfile(): Generator<any, void, any> {
  yield takeEvery(UPDATE_USER_PROFILE, updateUserProfileSaga);
}

function* watchChangeEmail(): Generator<any, void, any> {
  yield takeEvery(CHANGE_EMAIL, changeEmailSaga);
}

function* watchChangePassword(): Generator<any, void, any> {
  yield takeEvery(CHANGE_PASSWORD, changePasswordSaga);
}

function* watchFetchSubscription(): Generator<any, void, any> {
  yield takeLatest(FETCH_SUBSCRIPTION, fetchSubscriptionSaga);
}

function* watchCancelSubscription(): Generator<any, void, any> {
  yield takeEvery(CANCEL_SUBSCRIPTION, cancelSubscriptionSaga);
}

function* watchReactivateSubscription(): Generator<any, void, any> {
  yield takeEvery(REACTIVATE_SUBSCRIPTION, reactivateSubscriptionSaga);
}

function* watchFetchPaymentMethods(): Generator<any, void, any> {
  yield takeLatest(FETCH_PAYMENT_METHODS, fetchPaymentMethodsSaga);
}

function* watchUpdatePaymentMethod(): Generator<any, void, any> {
  yield takeEvery(UPDATE_PAYMENT_METHOD, updatePaymentMethodSaga);
}

function* watchAddPaymentMethod(): Generator<any, void, any> {
  yield takeEvery(ADD_PAYMENT_METHOD, addPaymentMethodSaga);
}

function* watchDeletePaymentMethod(): Generator<any, void, any> {
  yield takeEvery(DELETE_PAYMENT_METHOD, deletePaymentMethodSaga);
}

function* watchSetDefaultPaymentMethod(): Generator<any, void, any> {
  yield takeEvery(SET_DEFAULT_PAYMENT_METHOD, setDefaultPaymentMethodSaga);
}

function* watchFetchBillingHistory(): Generator<any, void, any> {
  yield takeLatest(FETCH_BILLING_HISTORY, fetchBillingHistorySaga);
}

function* watchDownloadInvoice(): Generator<any, void, any> {
  yield takeEvery(DOWNLOAD_INVOICE, downloadInvoiceSaga);
}

function* watchCloseAccount(): Generator<any, void, any> {
  yield takeEvery(CLOSE_ACCOUNT, closeAccountSaga);
}

// Root saga
export function* accountSaga(): Generator<any, void, any> {
  yield all([
    watchFetchAccountInfo(),
    watchFetchUserProfile(),
    watchUpdateUserProfile(),
    watchChangeEmail(),
    watchChangePassword(),
    watchFetchSubscription(),
    watchCancelSubscription(),
    watchReactivateSubscription(),
    watchFetchPaymentMethods(),
    watchUpdatePaymentMethod(),
    watchAddPaymentMethod(),
    watchDeletePaymentMethod(),
    watchSetDefaultPaymentMethod(),
    watchFetchBillingHistory(),
    watchDownloadInvoice(),
    watchCloseAccount(),
  ]);
} 